// Vue 2 directive to enable arrow-key navigation between inputs inside a container
// Usage: v-arrow-nav on a wrapper element. Optional value: { selector?: string }

function isVisible(el) {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
  return true;
}

function isDisabled(el) {
  return el.disabled || el.readOnly || el.getAttribute('aria-disabled') === 'true';
}

function defaultSelector() {
  return [
    'input:not([type="hidden"])',
    'textarea',
    'select',
    '[contenteditable="true"]',
    '.multiselect input',
  ].join(',');
}

function getFocusable(container, selector) {
  const sel = selector || defaultSelector();
  let nodes = Array.from(container.querySelectorAll(sel));
  nodes = nodes.map((el) => {
    const isMultiRoot = el.classList && el.classList.contains('multiselect');
    if (isMultiRoot) {
      const inner = el.querySelector('input');
      if (inner) return inner;
    }
    return el;
  });
  const seen = new Set();
  const unique = [];
  for (const el of nodes) {
    if (!el || seen.has(el)) continue;
    seen.add(el);
    unique.push(el);
  }
  return unique.filter((el) => isVisible(el) && !isDisabled(el));
}

function center(el) {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width, h: r.height };
}

function caretAtBoundary(target, dir) {
  const tag = (target.tagName || '').toLowerCase();
  const isTextual = tag === 'input' || tag === 'textarea';
  if (!isTextual) return true;

  try {
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const len = (target.value || '').length;
    if (dir === 'left' || dir === 'up') {
      return start === 0 && end === 0;
    }
    if (dir === 'right' || dir === 'down') {
      return end === len;
    }
  } catch (e) {
    return true;
  }
  return true;
}

function requiresBoundaryCheck(tag, dir) {
  // Horizontal: boundary required; Vertical: only for textarea
  if (dir === 'left' || dir === 'right') return true;
  if (dir === 'up' || dir === 'down') return tag === 'textarea';
  return false;
}

function findNext(container, currentEl, dir, selector) {
  const items = getFocusable(container, selector);
  const idx = items.indexOf(currentEl);
  if (idx === -1) return null;

  const curr = center(currentEl);
  const candidates = [];
  for (const el of items) {
    if (el === currentEl) continue;
    const c = center(el);
    const dx = c.x - curr.x;
    const dy = c.y - curr.y;
    if (dir === 'right' && dx <= 1) continue;
    if (dir === 'left' && dx >= -1) continue;
    if (dir === 'down' && dy <= 1) continue;
    if (dir === 'up' && dy >= -1) continue;

    candidates.push({ el, dx, dy, c });
  }

  if (!candidates.length) return null;

  const thresholdY = Math.max(28, curr.h * 0.8);
  const thresholdX = Math.max(28, curr.w * 0.8);

  let filtered;
  if (dir === 'right' || dir === 'left') {
    filtered = candidates.filter((n) => Math.abs(n.dy) <= thresholdY);
  } else {
    filtered = candidates.filter((n) => Math.abs(n.dx) <= thresholdX);
  }
  const list = filtered.length ? filtered : candidates;

  list.sort((a, b) => {
    if (dir === 'right') return a.dx - b.dx || Math.abs(a.dy) - Math.abs(b.dy);
    if (dir === 'left') return b.dx - a.dx || Math.abs(a.dy) - Math.abs(b.dy);
    if (dir === 'down') return a.dy - b.dy || Math.abs(a.dx) - Math.abs(b.dx);
    if (dir === 'up') return b.dy - a.dy || Math.abs(a.dx) - Math.abs(b.dx);
    return 0;
  });

  return (list[0] && list[0].el) || null;
}

function fallbackNext(container, currentEl, dir, selector) {
  const items = getFocusable(container, selector);
  const idx = items.indexOf(currentEl);
  if (idx === -1) return null;
  if (dir === 'left') {
    return items[Math.max(0, idx - 1)] || null;
  }
  if (dir === 'right') {
    return items[Math.min(items.length - 1, idx + 1)] || null;
  }
  if (dir === 'up') {
    return items[Math.max(0, idx - 1)] || null;
  }
  if (dir === 'down') {
    return items[Math.min(items.length - 1, idx + 1)] || null;
  }
  return null;
}

function focusElement(el) {
  if (!el) return;
  try {
    el.focus();
    // Place caret at end for text inputs
    const tag = (el.tagName || '').toLowerCase();
    if ((tag === 'input' || tag === 'textarea') && !el.readOnly && !el.disabled) {
      var len = (el.value || '').length;
      if (typeof el.setSelectionRange === 'function') {
        el.setSelectionRange(len, len);
      }
    }
  } catch (e) {
  }
}

export default {
  inserted(el, binding) {
    const opts = (binding && binding.value) || {};
    const selector = opts.selector || null;

    const handler = function (e) {
      const key = e.key || e.keyCode;
      const target = e.target;
      const tag = (target.tagName || '').toLowerCase();
      const isInputLike =
        tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
      if (!isInputLike) return;

      let dir = null;
      if (key === 'ArrowLeft' || key === 37) dir = 'left';
      else if (key === 'ArrowRight' || key === 39) dir = 'right';
      else if (key === 'ArrowUp' || key === 38) dir = 'up';
      else if (key === 'ArrowDown' || key === 40) dir = 'down';
      else return;

      // Multiselect: pass vertical; allow horizontal
      if (tag === 'select') return;
      const multi = target.closest && target.closest('.multiselect');
      if (multi) {
        if (dir === 'up' || dir === 'down') return;
      }

      // Keep native step on number inputs
      if (tag === 'input' && (target.type === 'number' || target.inputMode === 'numeric')) {
        if (dir === 'up' || dir === 'down') return;
      }

      // Boundary: left/right always; up/down only for textarea
      const needBoundary = requiresBoundaryCheck(tag, dir) && !(multi && (dir === 'left' || dir === 'right'));
      if (needBoundary && !caretAtBoundary(target, dir)) return;

      let next = findNext(el, target, dir, selector);
      // Fallback only for horizontal; vertical never drifts
      if (!next && (dir === 'left' || dir === 'right')) {
        next = fallbackNext(el, target, dir, selector);
      }
      if (next) {
        e.preventDefault();
        e.stopPropagation();
        focusElement(next);
      }
    };

    el.__arrowNavHandler__ = handler;
    el.addEventListener('keydown', handler, true);
  },
  unbind(el) {
    if (el.__arrowNavHandler__) {
      el.removeEventListener('keydown', el.__arrowNavHandler__, true);
      delete el.__arrowNavHandler__;
    }
  },
};
